import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { Modal, useToast } from './ui';

export async function uploadFiles(companyId: string, files: File[], kind: 'invoice' | 'delivery_note' = 'invoice', source?: 'mobile') {
  const fd = new FormData();
  fd.append('companyId', companyId);
  fd.append('kind', kind);
  if (source) fd.append('source', source);
  for (const f of files) fd.append('file', f, f.name);
  const r = await api.form<{ results: Array<{ filename: string; error?: string; duplicate?: boolean; invoiceId?: string }> }>('/upload', fd);
  const ok = r.results.filter((x) => !x.error && !x.duplicate).length;
  const dup = r.results.filter((x) => x.duplicate).length;
  const bad = r.results.filter((x) => x.error);
  const parts = [`${ok} documento(s) recibido(s)`];
  if (dup) parts.push(`${dup} ya existía(n)`);
  if (bad.length) parts.push(`${bad.length} con error: ${bad.map((b) => `${b.filename} (${b.error})`).join('; ')}`);
  return { results: r.results, summary: parts.join(' · ') };
}

export function UploadButton() {
  const s = useSession();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="primary" onClick={() => setOpen(true)}>Subir facturas</button>
      {open && <UploadModal onClose={() => setOpen(false)} companyId={s.companyId} />}
    </>
  );
}

export function UploadModal({ onClose, companyId, kind = 'invoice' }: { onClose: () => void; companyId: string | null; kind?: 'invoice' | 'delivery_note' }) {
  const s = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const [cid, setCid] = useState(companyId ?? s.me?.companies[0]?.id ?? '');
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const company = s.me?.companies.find((c) => c.id === cid);

  const send = async (files: File[], source?: 'mobile') => {
    if (!files.length || !cid) return;
    setBusy(true);
    try {
      const r = await uploadFiles(cid, files, kind, source);
      toast(r.summary, r.results.some((x) => x.error) ? 'error' : undefined);
      onClose();
      if (kind === 'invoice') {
        const single = r.results.filter((x) => x.invoiceId);
        nav(single.length === 1 ? `/facturas/${single[0].invoiceId}` : '/');
      }
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={kind === 'invoice' ? 'Subir facturas' : 'Subir albaranes'} onClose={onClose}>
      {s.me!.companies.length > 1 && (
        <select value={cid} onChange={(e) => setCid(e.target.value)} aria-label="Empresa">
          {s.me!.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
      <div
        className={`dropzone ${over ? 'over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setOver(false); send([...e.dataTransfer.files]); }}
      >
        <p><strong>Arrastra aquí los archivos</strong></p>
        <p>PDF, fotos (JPG, PNG, WEBP) o facturas electrónicas XML (Facturae, UBL, CII). Hasta 20 MB por archivo.</p>
        <div className="row" style={{ justifyContent: 'center' }}>
          <button disabled={busy} onClick={() => input.current?.click()}>Elegir archivos</button>
          <button disabled={busy} onClick={() => camera.current?.click()}>Hacer foto</button>
        </div>
        <input ref={input} type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.xml,.xsig" onChange={(e) => send([...(e.target.files ?? [])])} />
        <input ref={camera} type="file" hidden accept="image/*" capture="environment" onChange={(e) => send([...(e.target.files ?? [])], 'mobile')} />
      </div>
      {company && kind === 'invoice' && (
        <p className="hint">También puedes reenviar las facturas por correo a <code>{company.inboxAddress}</code>.</p>
      )}
      {busy && <p className="muted">Subiendo…</p>}
    </Modal>
  );
}
